import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
// import AISupportChat from "../components/AISupportChat/AISupportChat.component";
import { ConversationSummary, IFreshSession } from "../components/AISupportChat/AISupportChat.types";
import { AGENT_API_PREFIX, AGENT_BASE_URLS } from "../components/AISupportChat/const";
import { useApiAgentChatAxios } from "../components/AISupportChat/useApiAgentChatAxios.hook";
import { buildAgentUrl, ensureTrailingSlash } from "../components/AISupportChat/utils";
import { trackOverlayView } from "../services/tracking";
import { useAuthStore } from "../stores/authStore";

interface AISupportChatContextType {
  isOpen: boolean;
  isPrelogin: boolean;
  sessionId: string;
  openChat: (isPreLogin?:boolean) => void;
  closeChat: () => void;
  conversationsList: ConversationSummary[],
  isLoadingConversations: boolean;
  isCreatingConversation: boolean;
  providerError?: string,
  createNewConversation: () => void,
  deleteConversation: (sid:string) => void,
  selectConversation: (sid:string) => void,
  freshSession: IFreshSession,
  loadConversationsList: () => Promise<void>
  // setConversations: Dispatch<SetStateAction<ConversationSummary[]>>
  updateConversationMeta: (sid: string, patch: Partial<ConversationSummary>) => void,
  removeConversationFromList: (sid: string) => void,
}


export const AISupportChatContext = createContext<AISupportChatContextType | undefined>(
  undefined
);

export const useAISupportChat = (): AISupportChatContextType => {
  const context = useContext(AISupportChatContext);
  if (!context) {
    throw new Error(
      "useAISupportChat must be used inside AISupportChatProvider"
    );
  }
  return context;
};

export const AISupportChatProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [sessionId, setSessionId] = useState("");
  const sessionToken = useAuthStore(state => state.sessionToken);
  const [providerError, setProviderError] = useState("");
  const [isPrelogin, setIsPrelogin] = useState(false);

  //**** history conversations sidebar states ****//
  const [conversationsList, setConversationsList] = useState<ConversationSummary[]>([]);
  const [isCreatingConversation, setIsCreatingConversation] = useState(false);

  const [freshSession, setFreshSession] = useState<IFreshSession>({
    isFresh: false,
    id: ""
  });
  const [isLoadingConversations, setIsLoadingConversations] = useState<boolean>(false);

  const userData = useAuthStore(state => state.userData);
  const authToken = useAuthStore(state => state.session?.getAccessToken().getJwtToken());
  const userId = userData?.id_user;
  const { apiInstance, fetchSessionInfo } = useApiAgentChatAxios(sessionId);

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;
  const userMgmtData = useAuthStore(state => state.userMgmtData);

  const corporateId = useAuthStore(state => state.userData?.organization_id) ?? userMgmtData?.id?.toString() ?? "";
  
  // only support is available at the moment
  const endpoint = `${ensureTrailingSlash(AGENT_BASE_URLS.SUPPORT)}${AGENT_API_PREFIX.SUPPORT}/conversations`;


  // Il session_id della conversazione è sempre un UUID generato lato client.
  // Ne viene generato uno nuovo all'avvio e ad ogni cambio di sessione
  // applicativa (login/refresh/logout): nuova sessione = nuova conversazione.
  
  const openChat = (isPrelogin?:boolean) => {
    isPrelogin && setIsPrelogin(true);
    setIsOpen(true);
    setConversationsList([]);
    if (!sessionId) setSessionId(crypto.randomUUID());
  };
  
  useEffect(() => {
    if (isOpen) {
      trackOverlayView("overlay_chat_ai");
    }
  }, [isOpen]);
  
  const closeChat = () => {
    setIsOpen(false);
  };

  const loadConversationsList = useCallback(
    async () => {
      if (!userId || !corporateId) return;
      const endpoint = buildAgentUrl("sessions") + `${userId}`;
      
      if (!endpoint) {
        setProviderError("agentNotConfigured");
        return;
      }
      setIsLoadingConversations(true);
      try {
        const res = await apiInstance.get(endpoint, {
          params: { corporate_id: corporateId }
        });
        const sessionIds: string[] = res.data?.session_ids ?? [];
        const infos = await Promise.all(
          sessionIds.map((id) => fetchSessionInfo(id, corporateId))
        );
        const list: ConversationSummary[] = sessionIds
          .map((id, i) => ({
            session_id: id,
            ...infos[i]
          }))
          .filter((c) => c.message_count !== 0);
        list.sort((a, b) => {
          const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
          const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
          return tb - ta;
        });
        
        setConversationsList((prev) => {
          const activeSid = sessionIdRef.current;
          const pendingActive = prev.find(
            (c) =>
              c.session_id === activeSid &&
              !list.some((s) => s.session_id === activeSid)
          );
          return pendingActive ? [pendingActive, ...list] : list;
        });
      } catch (error) {
        console.error("[ChatAgent] Failed to fetch conversations", error);
        setProviderError("conversationsListFailed");
      } finally {
        setIsLoadingConversations(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [userId, authToken]
  );

  const createNewConversation = async () => {
    
    if (!endpoint) {
      setProviderError("agentNotConfigured");
      return null;
    }

    setIsCreatingConversation(true);
    try {
      const res = await apiInstance.post(endpoint);
      const newSid: string | undefined = res.data?.session_id;
      if (!newSid) {
        setProviderError("createConversationFailed");
        return null;
      }
      setFreshSession({
        id: newSid,
        isFresh: false
      });
      setSessionId(newSid);
      return;
    } catch (error) {
      console.error("[ChatAgent] Failed to create conversation", error);
      setProviderError("createConversationFailed");
      return null;
    } finally {
      setIsCreatingConversation(false);
    }
  };

  const selectConversation = useCallback((sid: string) => {
    setFreshSession({
      isFresh: false,
      id: ""
    });
    setSessionId(sid);
  }, []);

  const deleteConversation = useCallback(
    async (sid: string) => {
      const endpoint = buildAgentUrl("sessions") + sid;

      if (!endpoint) return;

      const deletedItem = conversationsList.find((c) => c.session_id === sid);
      const wasActive = sid === sessionId;

      setConversationsList((prev) => prev.filter((c) => c.session_id !== sid));

      if (wasActive) {
        setSessionId("");
        setFreshSession({
          id: "",
          isFresh: true
        });
        createNewConversation();
      }
      try {
        await apiInstance.delete(endpoint, {
          params: { corporate_id: corporateId }
        });
      } catch (error) {
        console.error("[ChatAgent] Failed to delete conversation", error);
        if (deletedItem) {
          setConversationsList((prev) => {
            if (prev.some((c) => c.session_id === sid)) return prev;
            return [...prev, deletedItem].sort((a, b) => {
              const ta = a.updated_at ? new Date(a.updated_at).getTime() : 0;
              const tb = b.updated_at ? new Date(b.updated_at).getTime() : 0;
              return tb - ta;
            });
          });
        }
        setProviderError("deleteConversationFailed");
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [authToken, corporateId, sessionId, conversationsList]
  );

  const updateConversationMeta = useCallback(
    (sid: string, patch: Partial<ConversationSummary>) => {
      setConversationsList((prev) => {
        if (prev.some((c) => c.session_id === sid)) {
          return prev.map((c) => (c.session_id === sid ? { ...c, ...patch } : c));
        }
        return [{ session_id: sid, ...patch }, ...prev];
      });
    },
    []
  );

  const removeConversationFromList = useCallback((sid: string) => {
    setConversationsList((prev) => prev.filter((c) => c.session_id !== sid));
  }, []);
  
  useEffect(() => {
    setSessionId(crypto.randomUUID());
  }, [sessionToken]);

  return (
    <AISupportChatContext.Provider
      value={ { 
        isOpen,
        isPrelogin,
        sessionId,
        openChat, 
        closeChat,
        conversationsList,
        isLoadingConversations,
        isCreatingConversation,
        providerError,
        createNewConversation,
        deleteConversation,
        freshSession,
        selectConversation,
        loadConversationsList,
        removeConversationFromList,
        updateConversationMeta
      } }
    >
      { children }
      { /* <AISupportChat /> */ }
    </AISupportChatContext.Provider>
  );
};
